import { isDefined } from '@sgme/fp';
import { HStack } from '@sgme/ui';
import clsx from 'clsx';

import './DatePickerWithTenorNext.css';

import { type ComponentProps, useState } from 'react';
import { useIntl } from 'react-intl';

import { SgDatePicker } from '@/components/sg-design-system/components/input/SgDatePicker';
import type { Color } from '@/components/sg-design-system/types/colors.ts';
import type { Size } from '@/components/sg-design-system/types/layout.ts';
import { useAppSelector } from '@/store/hooks.ts';
import { getUserPreferences } from '@/store/userPreferences/slice.ts';
import { ALL_CALENDAR_TENORS, type ISODate, type Tenor } from '@/types/date.ts';
import { DATE_FORMAT_TEXT } from '@/utils/date.ts';

export type DatePickerWithTenorNextProps = {
  date?: string;
  tenor?: string;
  placeholder?: string;
  onChange: (dateStringOrTenor: ISODate | Tenor) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  'data-e2e'?: string;
  size?: Size;
  allTenors?: string[];
  className?: string;
  disabled?: boolean;
  readonly?: boolean;
  validationState?: ComponentProps<typeof SgDatePicker>['validationState'];
  validationMessage?: ComponentProps<typeof SgDatePicker>['validationMessage'];
  color?: Color;

  // cf https://developer.sgmarkets.com/docs/design-system/v5/components/date-pickers.html
  // but the string can be a comma-separated list of the documented values, allowing to disable dates using more than 1 preset
  disablePresets?: ComponentProps<typeof SgDatePicker>['disablePresets'];
};

export const DatePickerWithTenorNext = (props: DatePickerWithTenorNextProps) => {
  const {
    onBlur,
    onFocus,
    date,
    tenor,
    placeholder,
    onChange,
    allTenors,
    'data-e2e': dataE2e,
    size = 'sm',
    className,
    validationState,
    validationMessage,
    disablePresets,
    disabled = false,
    readonly = false,
    color,
  } = props;

  const { dateInputCultureInfo } = useAppSelector(getUserPreferences);
  const { locale } = useIntl();

  const handleDateSelected = (date: string) => {
    onChange(date);
  };

  const handleTenorSelected = (tenor: string) => {
    onChange(tenor);
  };

  const handleTenorClick = async () => {
    setOpen((open) => !open);
  };

  const customPresets = (allTenors ?? ALL_CALENDAR_TENORS) as string[];

  const [open, setOpen] = useState(false);

  const getComputedLocale = () => {
    if (dateInputCultureInfo === 'en') {
      return 'en-US';
    }
    if (dateInputCultureInfo === 'fr') {
      return 'fr-FR';
    }
    if (locale === 'en') {
      return 'en-GB';
    }
    return 'fr-FR';
  };

  return (
    <HStack fullWidth className="overflow-visible" data-e2e={dataE2e} aria-invalid={validationState === 'invalid'}>
      <div className="flex-grow-1 position-relative" style={{ minWidth: 0 }}>
        <SgDatePicker
          isOpen={open}
          locale={locale === 'en' ? 'en-GB' : 'fr-FR'}
          dateInputCultureInfo={getComputedLocale()}
          onIsOpenChange={(isOpen) => {
            setOpen(isOpen);
          }}
          className={clsx(className, 'fx-date-picker')}
          value={date}
          onChange={handleDateSelected}
          onCustomPresetClick={handleTenorSelected}
          color={color}
          showClearButton={false}
          size={size}
          onFocus={onFocus}
          onBlurInput={onBlur}
          yearControls
          placeholder={placeholder}
          customPresets={customPresets} // WARNING: the position of the grid can be out of the screen -> waiting Alicia's response
          presetsDisplayGrid={3}
          data-e2e={`${dataE2e}-date`}
          validationState={validationState}
          validationMessage={validationMessage}
          validationDisplay="tooltip"
          disablePresets={disablePresets}
          disabled={disabled}
          dateFormat={DATE_FORMAT_TEXT}
          readonly={readonly}
        />
      </div>

      <button
        onClick={handleTenorClick}
        type="button"
        className={clsx('btn btn-discreet-primary d-flex align-items-center justify-content-center flex-shrink-0', {
          'btn-sm': size === 'sm',
          'btn-md': size === 'md',
          'btn-lg': size === 'lg',
          disabled: disabled,
        })}
        data-e2e={`${dataE2e}-tenor-button`}
        style={{ minWidth: '30px' }}
        tabIndex={-1}
        disabled={readonly}
      >
        {isDefined(tenor) ? tenor : <i className="icon icon-sm">date_range</i>}
      </button>
    </HStack>
  );
};
